import { useQueryClient } from '@tanstack/react-query';
import { useProtectedMutation } from '@/hooks/use-protected-mutation';
import { useProtectedQuery } from '@/hooks/use-protected-query';
import { fetchData, post, patch } from '@/utils/api-handler';
import { toast } from 'sonner';
import {
  COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY,
  COMMAND_V2_SCHEDULES_QUERY_KEY,
} from '@/hooks/use-command-v2-schedules';
import {
  COMMAND_SCHEDULER_API_PATH,
  COMMAND_SCHEDULER_ENTITY_TYPE,
  COMMAND_SCHEDULER_SESSION_ENTITY_TYPE,
  COMMAND_SCHEDULER_JOB_NAME,
  COMMAND_SCHEDULER_SCHEDULE_TYPE,
  COMMAND_SCHEDULER_SCHEDULE_TYPE_RECURRING,
} from '@/components/aios-command/chat/chat-constants';
import {
  cronExpressionWithUtcTime,
  type CommandScheduleMode,
} from '@/utils/command-scheduler';

export const COMMAND_SCHEDULER_JOB_QUERY_KEY = 'command-scheduler-job';

export function useCommandScheduleJob(jobId: string | null, enabled: boolean) {
  return useProtectedQuery<ScheduledJob>({
    queryKey: [COMMAND_SCHEDULER_JOB_QUERY_KEY, jobId],
    queryFn: async () => {
      const data = await fetchData(`${COMMAND_SCHEDULER_API_PATH}/${jobId}`);
      return data as ScheduledJob;
    },
    enabled: enabled && Boolean(jobId),
    requiredPermissions: [],
  });
}

export type CreateCommandScheduleJobVariables = {
  sessionId: string;
  newSession?: boolean;
  agentId: string;
  prompt: string;
  description: string;
  runAt: Date;
  fileIds: string[];
  userId: string;
  organizationId: string;
  commandTeamId: string;
  analystTeamId: string | null;
  salesTeamId: string | null;
  supportTeamId: string | null;
  tasksTeamId: string | null;
  computerUseTeamId: string | null;
  scheduleType?: CommandScheduleMode;
  cronExpression?: string | null;
};

function buildScheduledJobBody({
  sessionId,
  agentId,
  prompt,
  description,
  runAt,
  fileIds,
  newSession,
  userId,
  organizationId,
  commandTeamId,
  analystTeamId,
  salesTeamId,
  supportTeamId,
  tasksTeamId,
  computerUseTeamId,
  scheduleType = 'one_time',
  cronExpression = null,
}: CreateCommandScheduleJobVariables) {
  const isRecurring = scheduleType === 'recurring';
  const scheduleAsCommandAgent = newSession !== false;
  const descriptionTrimmed = description.trim();
  const dtoPayload = {
    session_id: sessionId,
    agent_id: agentId,
    command_team_id: commandTeamId,
    organization_id: organizationId,
    user_id: userId,
    content: [{ type: 'text', text: prompt }],
    file_ids: fileIds.length > 0 ? fileIds : null,
    analyst_team_id: analystTeamId?.trim() || null,
    sales_team_id: salesTeamId?.trim() || null,
    support_team_id: supportTeamId?.trim() || null,
    tasks_team_id: tasksTeamId?.trim() || null,
    computer_use_team_id: computerUseTeamId?.trim() || null,
  };
  const commandPayload = {
    dto: dtoPayload,
    new_session: scheduleAsCommandAgent ? Boolean(newSession) : false,
  };
  const body: Record<string, unknown> = {
    job_name: COMMAND_SCHEDULER_JOB_NAME,
    keyword_arguments: {
      command: JSON.stringify(commandPayload),
    },
    description: descriptionTrimmed === '' ? null : descriptionTrimmed,
    schedule_type: isRecurring ? COMMAND_SCHEDULER_SCHEDULE_TYPE_RECURRING : COMMAND_SCHEDULER_SCHEDULE_TYPE,
    entity_type: scheduleAsCommandAgent
      ? COMMAND_SCHEDULER_ENTITY_TYPE
      : COMMAND_SCHEDULER_SESSION_ENTITY_TYPE,
    entity_id: scheduleAsCommandAgent ? agentId : sessionId,
  };
  if (isRecurring) {
    body.cron_expression =
      cronExpression ? cronExpressionWithUtcTime(cronExpression, runAt) : null;
    body.run_at = null;
  } else {
    body.cron_expression = null;
    body.run_at = runAt.toISOString();
  }
  return body;
}

export function useCreateCommandScheduleJob() {
  const queryClient = useQueryClient();
  return useProtectedMutation<{ id: string }, unknown, CreateCommandScheduleJobVariables>({
    requiredPermissions: ['CREATE_COMMAND_MESSAGE'],
    mutationFn: async (input) => {
      const response = await post(COMMAND_SCHEDULER_API_PATH, buildScheduledJobBody(input));
      return response.data as { id: string };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [COMMAND_V2_SCHEDULES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
      const when = variables.runAt.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ');
      if (variables.scheduleType === 'recurring') {
        toast.success('Recurring command scheduled', {
          description: `Next run ${when} (${tz})`,
        });
        return;
      }
      toast.success('Command scheduled', {
        description: `Runs at ${when} (${tz})`,
      });
    },
    onError: (error: unknown) => {
      const err = error as {
        message?: string;
        response?: { data?: { message?: string; detail?: unknown; code?: string } };
      };
      if (err?.message === 'PERMISSION_DENIED') return;
      const data = err?.response?.data;
      const detail = data?.detail;
      const detailMsg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail) && detail[0] && typeof (detail[0] as { msg?: string }).msg === 'string'
            ? (detail[0] as { msg: string }).msg
            : undefined;
      toast.error('Failed to schedule command', {
        description: data?.message || detailMsg || err?.message || 'Please try again.',
      });
    },
  });
}

export type UpdateCommandScheduleJobVariables = {
  jobId: string;
  description: string;
  runAt: Date;
  scheduleType?: CommandScheduleMode;
  cronExpression?: string | null;
  keywordArguments: Record<string, unknown>;
};

function buildUpdatedScheduledJobBody({
  description,
  runAt,
  scheduleType = 'one_time',
  cronExpression = null,
  keywordArguments,
}: Omit<UpdateCommandScheduleJobVariables, 'jobId'>) {
  const isRecurring = scheduleType === 'recurring';
  const descriptionTrimmed = description.trim();
  const body: Record<string, unknown> = {
    description: descriptionTrimmed === '' ? null : descriptionTrimmed,
    keyword_arguments: keywordArguments,
  };
  if (isRecurring) {
    body.cron_expression = cronExpression
      ? cronExpressionWithUtcTime(cronExpression, runAt)
      : null;
  } else {
    body.run_at = runAt.toISOString();
  }
  return body;
}

export function useUpdateCommandScheduleJob() {
  const queryClient = useQueryClient();
  return useProtectedMutation<{ id: string }, unknown, UpdateCommandScheduleJobVariables>({
    requiredPermissions: ['CREATE_COMMAND_MESSAGE'],
    mutationFn: async ({ jobId, ...input }) => {
      const response = await patch(
        `${COMMAND_SCHEDULER_API_PATH}/${jobId}`,
        buildUpdatedScheduledJobBody(input),
      );
      return response.data as { id: string };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [COMMAND_V2_SCHEDULES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
      queryClient.invalidateQueries({
        queryKey: [COMMAND_SCHEDULER_JOB_QUERY_KEY, variables.jobId],
      });
      toast.success('Schedule updated', {
        description: 'Your scheduled prompt has been saved.',
      });
    },
    onError: (error: unknown) => {
      const err = error as {
        message?: string;
        response?: { data?: { message?: string; detail?: unknown } };
      };
      if (err?.message === 'PERMISSION_DENIED') return;
      const data = err?.response?.data;
      const detail = data?.detail;
      const detailMsg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail) && detail[0] && typeof (detail[0] as { msg?: string }).msg === 'string'
            ? (detail[0] as { msg: string }).msg
            : undefined;
      toast.error('Failed to update schedule', {
        description: data?.message || detailMsg || err?.message || 'Please try again.',
      });
    },
  });
}

function schedulerMutationErrorMessage(error: unknown): string {
  const err = error as {
    message?: string;
    response?: { data?: { message?: string; detail?: unknown } };
  };
  const data = err?.response?.data;
  const detail = data?.detail;
  const detailMsg =
    typeof detail === 'string'
      ? detail
      : Array.isArray(detail) && detail[0] && typeof (detail[0] as { msg?: string }).msg === 'string'
        ? (detail[0] as { msg: string }).msg
        : undefined;
  return data?.message || detailMsg || err?.message || 'Please try again.';
}

function useInvalidateCommandScheduleQueries() {
  const queryClient = useQueryClient();
  return (jobId: string) => {
    queryClient.invalidateQueries({ queryKey: [COMMAND_V2_SCHEDULES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    queryClient.invalidateQueries({ queryKey: [COMMAND_SCHEDULER_JOB_QUERY_KEY, jobId] });
  };
}

export function usePauseCommandScheduleJob() {
  const invalidate = useInvalidateCommandScheduleQueries();
  return useProtectedMutation<{ paused: string }, unknown, { jobId: string }>({
    requiredPermissions: ['CREATE_COMMAND_MESSAGE'],
    mutationFn: async ({ jobId }) => {
      const response = await patch(`${COMMAND_SCHEDULER_API_PATH}/${jobId}/pause`, {});
      return response.data as { paused: string };
    },
    onSuccess: (_data, { jobId }) => {
      invalidate(jobId);
      toast.success('Schedule paused', {
        description: 'It keeps its settings — resume any time.',
      });
    },
    onError: (error: unknown) => {
      if ((error as { message?: string })?.message === 'PERMISSION_DENIED') return;
      toast.error('Failed to pause schedule', {
        description: schedulerMutationErrorMessage(error),
      });
    },
  });
}

export function useResumeCommandScheduleJob() {
  const invalidate = useInvalidateCommandScheduleQueries();
  return useProtectedMutation<{ resumed: string }, unknown, { jobId: string }>({
    requiredPermissions: ['CREATE_COMMAND_MESSAGE'],
    mutationFn: async ({ jobId }) => {
      const response = await patch(`${COMMAND_SCHEDULER_API_PATH}/${jobId}/resume`, {});
      return response.data as { resumed: string };
    },
    onSuccess: (_data, { jobId }) => {
      invalidate(jobId);
      toast.success('Schedule resumed', {
        description: 'This prompt will run on its next scheduled time.',
      });
    },
    onError: (error: unknown) => {
      if ((error as { message?: string })?.message === 'PERMISSION_DENIED') return;
      toast.error('Failed to resume schedule', {
        description: schedulerMutationErrorMessage(error),
      });
    },
  });
}
